import type { ITradeIn } from '@makemydeal/dr-platform-types';
import type { Deals, GroupedDeal, SellingVehicle, TradeInVehicle, DealTrades } from '../types';

// ─── Deal Utils ───────────────────────────────────────────────────────────────

export const formatVehicleName = (vehicle?: SellingVehicle | TradeInVehicle): string => {
    const parts = [vehicle?.year ?? '', vehicle?.make ?? '', vehicle?.model ?? '', vehicle?.trim ?? ''];
    return parts.filter(Boolean).join(' ') || 'Unknown Vehicle';
};

export const groupedDealsToArray = (
    deals: Deals[]
): Array<{
    dealId: string;
    deals: Deals[];
    sellingVehicle: SellingVehicle | undefined;
    tradeInVehicles?: { tradeIns: TradeInVehicle[] };
}> => {
    // Defensive check: if input is not an array, return empty array
    if (!Array.isArray(deals)) return [];
    // Group by dealId → vin → dealType, keeping only the latest version per type
    const grouped: Record<string, Record<string, Record<string, Deals>>> = {};

    for (const deal of deals) {
        const { dealId, dealType, sellingVehicle } = deal;
        const vin = sellingVehicle?.vin ?? 'unknown';

        if (!grouped[dealId]) grouped[dealId] = {};
        if (!grouped[dealId][vin]) grouped[dealId][vin] = {};

        const existing = grouped[dealId][vin][dealType];
        // Keep only the latest version per dealType (by versionCreatedOn)
        if (!existing || new Date(deal.versionCreatedOn) > new Date(existing.versionCreatedOn)) {
            grouped[dealId][vin][dealType] = deal;
        }
    }

    // Flatten into GroupedDeal array: one entry per dealId+vin combo
    const groups = Object.entries(grouped).flatMap(([dealId, byVin]) =>
        Object.entries(byVin).map(([, byType]) => {
            const dealsForGroup = Object.values(byType).sort((a, b) => a.dealType.localeCompare(b.dealType));

            const sellingVehicle = dealsForGroup[0].sellingVehicle;

            const allTradeIns = dealsForGroup.flatMap((deal) => deal.tradeInVehicles?.tradeIns ?? []);
            const tradeInVehicles = allTradeIns.length > 0 ? { tradeIns: allTradeIns } : undefined;

            // Determine the most recent lastModifiedDate across all deals in this group for sorting
            const latestModifiedDate = dealsForGroup.reduce((latest, deal) => {
                return new Date(deal.lastModifiedDate) > new Date(latest) ? deal.lastModifiedDate : latest;
            }, dealsForGroup[0].lastModifiedDate);

            return {
                dealId,
                sellingVehicle,
                tradeInVehicles,
                deals: dealsForGroup,
                latestModifiedDate
            };
        })
    );

    // Sort groups so the most recently updated vehicle card appears first
    return groups
        .sort((a, b) => new Date(b.latestModifiedDate).getTime() - new Date(a.latestModifiedDate).getTime())
        .map(({ latestModifiedDate: _omit, ...rest }) => rest);
};

// ─── Trade-In Utils ──────────────────────────────────────────────────────────

/**
 * Collect unique trade-in vehicles across all grouped deals, capped at a max count.
 * Uniqueness is determined by formatted vehicle name (year + make + model).
 * Deals are already sorted most-recent-first, so the first occurrence of each
 * unique name is the latest one.
 */
export const getUniqueTradeIns = (
    deals: Array<{ tradeInVehicles?: { tradeIns: TradeInVehicle[] } }>,
    max = 3
): TradeInVehicle[] => {
    const dedupedNames = new Set<string>();

    return deals
        .flatMap((groupedDeal) => groupedDeal.tradeInVehicles?.tradeIns ?? [])
        .filter((tradeIn) => {
            const name = formatVehicleName(tradeIn);
            if (dedupedNames.has(name)) return false;
            dedupedNames.add(name);
            return true;
        })
        .slice(0, max);
};

export const buildSourceDealIdentifiers = (deals: GroupedDeal[]): Array<{ dealXgId: string; version: string }> => {
    const seenVins = new Set<string>();
    const seenPairs = new Set<string>();
    const sourceDeals: Array<{ dealXgId: string; version: string }> = [];

    deals.forEach((groupedDeal: GroupedDeal) => {
        groupedDeal.deals?.forEach((dealVersion: any) => {
            const tradeIns = dealVersion?.tradeInVehicles?.tradeIns ?? [];
            tradeIns.forEach((tradeIn: { vin?: string }) => {
                const vin = tradeIn?.vin;
                if (!vin || seenVins.has(vin)) return;

                seenVins.add(vin);
                const pair = `${dealVersion.dealId}:${dealVersion.version}`;
                if (seenPairs.has(pair)) return;

                seenPairs.add(pair);
                sourceDeals.push({ dealXgId: dealVersion.dealId, version: dealVersion.version });
            });
        });
    });

    return sourceDeals;
};

// ─── Deal Update Utils ───────────────────────────────────────────────────────

/**
 * Updates deals with trade-in information from trade data array
 * Matches deals with trades by dealXgId and updates the tradeIns property
 *
 * @param deals - Array of deals to update
 * @param trades - Array of trade data containing dealXgId and tradeIns
 * @returns New array of deals with updated trade-in information
 */
export const updateDealsWithTradeIns = (deals: GroupedDeal[], trades: DealTrades[]): GroupedDeal[] => {
    const tradesByDealId = trades.reduce((acc, { dealXgId, tradeIns }) => {
        const merged = acc.get(dealXgId) ?? [];
        const seenVins = new Set(merged.map((trade) => trade.vin).filter((vin): vin is string => !!vin));

        tradeIns.forEach((trade) => {
            if (!trade.vin || !seenVins.has(trade.vin)) {
                merged.push(trade);
                if (trade.vin) seenVins.add(trade.vin);
            }
        });

        acc.set(dealXgId, merged);
        return acc;
    }, new Map<string, ITradeIn[]>());

    return deals.map((deal: GroupedDeal) => {
        const merged = tradesByDealId.get(deal.dealId);
        return merged ? { ...deal, tradeIns: merged } : deal;
    });
};

// ─── Date Utils ───────────────────────────────────────────────────────────────

/**
 * Format date to "Feb 2, 2026 at 9:30 AM"
 * @param dateString - ISO date string to format
 * @returns Formatted date string or empty string if invalid
 */
export const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    };

    const formatted = date.toLocaleString('en-US', options);
    // Replace comma after 4-digit year with " at"
    return formatted.replace(/(\d{4}),\s+/, '$1 at ');
};

/**
 * Get all trade-in vehicles from the customer vehicles state.
 * @param state - CustomerVehiclesState containing grouped deals with trade-in information
 * @returns Array of all trade-in vehicles
 */

export const getAllTradeIns = (deals: GroupedDeal[] | undefined): ITradeIn[] => {
    const allTradeIns: ITradeIn[] = [];
    (deals || []).forEach((deal: GroupedDeal) => {
        if (Array.isArray(deal?.tradeIns)) {
            allTradeIns.push(...deal.tradeIns);
        }
    });
    return allTradeIns;
};

/**
 * Collects trade-ins across all grouped deals and deduplicates by VIN.
 * Keeps the first occurrence of each VIN (deals are sorted most-recent-first).
 * VIN-less trades are always included since they can't be deduplicated.
 */
export const getDeduplicatedTradeIns = (deals: GroupedDeal[] | undefined): ITradeIn[] => {
    const allTradeIns = getAllTradeIns(deals);
    const processedVins = new Set<string>();
    return allTradeIns.filter((trade: ITradeIn) => {
        if (!trade.vin) return true;
        if (processedVins.has(trade.vin)) return false;
        processedVins.add(trade.vin);
        return true;
    });
};

export const areTradeInsEqual = (newSelected: Set<string>, currentDealVins: Set<string>): boolean => {
    return Array.from(newSelected).every((value) => currentDealVins.has(value)) && newSelected.size === currentDealVins.size;
};

/**
 * Determines whether a trade-in should display the "Appraised" badge.
 * A trade is appraised if it has both acv > 0 and value > 0.
 * VIN-based trades must also belong to the current deal.
 */
export const isAppraisedTrade = (
    tradeIn: { vin?: string },
    dealsTradeIns: ITradeIn[],
    isInCurrentDeal: (vin: string) => boolean
): boolean => {
    const { vin } = tradeIn;

    // VIN-based: must be in current deal and have financial data
    if (vin && !isInCurrentDeal(vin)) return false;

    // Find matching trade (by VIN if present, otherwise first VIN-less entry)
    const matchingTrade = dealsTradeIns.find((t) => (vin ? t.vin === vin : !t.vin));

    return matchingTrade ? (matchingTrade.acv ?? 0) > 0 && (matchingTrade.value ?? 0) > 0 : false;
};
